/* eslint-disable @sail/ui-use-tokens */
import * as React from 'react';
import {view, Spinner} from '@sail/ui';
import {createKeyframes} from '@sail/engine';
import type {ContentStateProps, ContentStateSize} from './ContentState';
import {ContentState} from './ContentState';

type Props = Omit<ContentStateProps, 'actions' | 'icon'>;

const getTitleFont = (size: ContentStateSize) => {
  switch (size) {
    case 'small':
      return 'label.medium';
    case 'medium':
      return 'label.medium';
    default:
      return 'heading.small.subdued';
  }
};

const getDescriptionFont = (size: ContentStateSize) => {
  switch (size) {
    case 'small':
      return 'label.small';
    case 'medium':
      return 'label.medium';
    default:
      return 'label.medium';
  }
};

// We want the spinner to be a fixed size and not to scale with Sail token
const getSpinnerSize = (size: ContentStateSize) => {
  switch (size) {
    case 'small':
      return '12px';
    case 'large':
      return '24px';
    default:
      return '24px';
  }
};

const SPINNER_ROTATION_DURATION = 700;

const adjustRotation = (startDegrees = 0) =>
  createKeyframes({
    0: {
      transform: `rotate(${startDegrees}deg)`,
    },
    100: {
      transform: `rotate(${startDegrees + 360}deg)`,
    },
  });

export const LoadingState = ({
  title,
  description,
  size = 'medium',
  ...rest
}: Props) => {
  // Across usages of LoadingState, we want the spinner to be in sync as sometimes loaders show up sequentially
  // We calculate where the animation should start based on global time so they remain seemingly in sync
  const rotationOffset =
    ((Date.now() % SPINNER_ROTATION_DURATION) / SPINNER_ROTATION_DURATION) *
    360;

  const rotationKeyFrames = adjustRotation(rotationOffset);

  return (
    <ContentState
      // If padding is not defined, we want to use a fixed padding that does not scale with Sail token
      padding="48px"
      size={size}
      icon={
        <Spinner
          data-testid="loading-state-spinner"
          size={size === 'small' ? 'small' : 'large'}
          css={{
            stack: 'x',
            animation: `${rotationKeyFrames} ${SPINNER_ROTATION_DURATION}ms linear infinite`,
            transitionProperty: 'opacity, transform',
            transitionTimingFunction: 'ease',
            transformOrigin: '50% 50%',
            transform: 'scaleX(-1)',
            height: getSpinnerSize(size),
            width: getSpinnerSize(size),
          }}
        />
      }
      title={
        title && (
          <view.span
            css={{
              color: 'subdued',
              font: getTitleFont(size),
              fontWeight: 'regular',
            }}
          >
            {title}
          </view.span>
        )
      }
      description={
        description && (
          <view.span
            css={{
              font: getDescriptionFont(size),
            }}
          >
            {description}
          </view.span>
        )
      }
      align="center"
      {...rest}
    />
  );
};

export default LoadingState;
